/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { Component, Type } from '@angular/core';

import {
    AviPermissionResource,
    IUpgradeProfile,
} from 'generated-types';
import { UpgradeProfile } from 'object-types';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';

interface IUpgradeProfileData {
    config: IUpgradeProfile;
}

export type TUpgradeProfileItem = typeof UpgradeProfileItem;

/**
 * @description Upgrade-Profile Item
 * @author Dhanashree Tilak
 */
export class UpgradeProfileItem extends withFullModalMixin(ObjectTypeItem) {
    public getConfig: () => IUpgradeProfile;
    public data: IUpgradeProfileData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'upgradeprofile',
            objectType: UpgradeProfile,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_UPGRADEPROFILE,
            id: 'default',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Method used to import lazy-loaded modal component.
     */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            UpdateProfileModalComponent,
        } = await import(
            /* webpackChunkName: "update-profile-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/update-profile-modal/update-profile-modal.component'
        );

        return UpdateProfileModalComponent as Type<Component>;
    }

    /** @override */
    public urlToSave(): string {
        return `/api/${this.objectName}`;
    }

    /** @override */
    public async loadConfig(): Promise<ng.IHttpResponse<IUpgradeProfile>> {
        const url = `/api/${this.objectName}`;

        this.cancelRequests('config');

        const response: ng.IHttpResponse<IUpgradeProfile> =
            await this.request('get', url, undefined, null, 'config');

        this.onConfigLoad_(response);

        return response;
    }

    /**
     * Method will load required Field on add or edit action.
     * @override
     */
    protected requiredFields(): string[] {
        return [
            'controller_params',
            'dry_run',
            'image',
            'pre_checks',
            'service_engine',
        ];
    }
}
